import React from 'react';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';
import { TenantContextProvider } from './TenantContextProvider';
import { AppInsightsProvider } from './AppInsightsProvider';
import { Toaster } from '@/shared/ui/sonner';

interface AppProvidersProps {
  readonly children: React.ReactNode;
}

/**
 * Composes all app-level providers in the required order:
 * QueryProvider → ThemeProvider → TenantContextProvider → AppInsightsProvider
 *
 * `AuthProvider` (MSAL bootstrap: `msalInstance.initialize()` + `MsalProvider`) was removed —
 * Entra Native Authentication (`core/auth/entraNativeAuthClient.ts`) needs no client
 * instance/init step, so there is nothing left to compose here; `RequireAuth` alone owns session
 * bootstrap (the "Angemeldet bleiben" boot re-exchange).
 *
 * The Sonner `<Toaster />` is mounted once here, inside `ThemeProvider` so its light/dark
 * variant follows the app theme, providing the toast surface for app-wide feedback (e.g. the
 * logout confirmation).
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryProvider>
      <ThemeProvider>
        <TenantContextProvider>
          <AppInsightsProvider>{children}</AppInsightsProvider>
        </TenantContextProvider>
        <Toaster />
      </ThemeProvider>
    </QueryProvider>
  );
}

